import { CircleDollarSign, MessageSquareText, Settings, ShieldCheck } from "lucide-react";
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@flint/ui";
import type { ReactNode } from "react";
import { Field, SectionCard } from "./section-card.js";
import { BillAttachmentsCard } from "./bill-attachments-card.js";
import type { BillTotals, NewBillFormValues } from "./types.js";
import { paiseToInrExact } from "../../../lib/format.js";

interface TaxApprovalSectionProps {
  values: NewBillFormValues;
  totals: BillTotals;
  onChange: <K extends keyof NewBillFormValues>(field: K, value: NewBillFormValues[K]) => void;
  /**
   * Bill id once the draft has been saved. `null` keeps the attachment
   * uploader disabled with a "save first" hint because there's no FK yet.
   */
  savedBillId: string | null;
  /** Company state name — used in the intra-state help text. Was
   *  hardcoded to "Maharashtra" before; now derived from `me.company.state`. */
  companyState: string;
}

export function TaxApprovalSection({
  values,
  totals,
  onChange,
  savedBillId,
  companyState,
}: TaxApprovalSectionProps) {
  const halfTax = totals.taxPaise / 2n;
  const tdsAmountPaise = computeTdsPaise(totals.subtotalPaise, values.tdsRate);
  const taxSummary = taxSummaryTile(values.taxMode, halfTax, totals.taxPaise);

  const handleTaxModeChange = (mode: NewBillFormValues["taxMode"]) => {
    onChange("taxMode", mode);

    if (mode === "exempt") {
      onChange(
        "lineItems",
        values.lineItems.map((row) => ({ ...row, taxRate: "0" })),
      );
      onChange("itcEligible", false);
      return;
    }

    if (values.taxMode === "exempt") {
      onChange(
        "lineItems",
        values.lineItems.map((row) => ({
          ...row,
          taxRate: row.taxRate.trim() === "" || row.taxRate === "0" ? "18" : row.taxRate,
        })),
      );
      onChange("itcEligible", true);
    }
  };

  return (
    <div className="grid gap-4">
      <SectionCard
        step="5"
        title="Tax, TDS & RCM"
        description="Auto-derived from vendor GSTIN + place of supply. Override if needed."
      >
        <p className="mb-2 text-xs font-semibold text-ink-2">Tax mode</p>
        <div className="grid gap-2 md:grid-cols-3">
          <TaxModeButton
            active={values.taxMode === "intra"}
            onClick={() => handleTaxModeChange("intra")}
          >
            CGST + SGST <span className="font-mono">9% + 9%</span>
          </TaxModeButton>
          <TaxModeButton
            active={values.taxMode === "inter"}
            onClick={() => handleTaxModeChange("inter")}
          >
            IGST <span className="font-mono">18%</span>
          </TaxModeButton>
          <TaxModeButton
            active={values.taxMode === "exempt"}
            onClick={() => handleTaxModeChange("exempt")}
          >
            Exempt / non-GST
          </TaxModeButton>
        </div>
        <p className="mt-2 text-xs text-muted">{taxModeHelp(values.taxMode, companyState)}</p>

        <ToggleRow
          title="Reverse charge (RCM) applies"
          body="For unregistered vendors, GTA services, or specified categories under CGST 9(3)/9(4). You pay the GST directly, not the vendor."
          checked={values.rcmApplies}
          onChange={(checked) => onChange("rcmApplies", checked)}
        />
        <ToggleRow
          title="Input tax credit eligible"
          body={`GST on this bill (${paiseToInrExact(totals.taxPaise)}) will be claimed in GSTR-3B against your output tax. Block if personal use or blocked under section 17(5).`}
          checked={values.itcEligible}
          onChange={(checked) => onChange("itcEligible", checked)}
        />

        <p className="mb-2 mt-5 text-xs font-semibold text-ink-2">
          TDS deduction (applied at payment time)
        </p>
        <div className="grid gap-3 md:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]">
          <Field label="TDS section">
            <Select
              value={values.tdsSection}
              onValueChange={(value) => {
                onChange("tdsSection", value);
                onChange("tdsRate", defaultTdsRate(value));
              }}
            >
              <SelectTrigger
                className="min-w-0 overflow-hidden text-sm [&>span:first-child]:block [&>span:first-child]:min-w-0 [&>span:first-child]:flex-1 [&>span:first-child]:truncate"
                title={tdsSectionLabel(values.tdsSection)}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="None">None - raw material goods</SelectItem>
                <SelectItem value="194Q">194Q - purchase of goods - 0.1%</SelectItem>
                <SelectItem value="194C">194C - contract / sub-contract - 1%</SelectItem>
                <SelectItem value="194J">194J - professional fees - 10%</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted">If applicable, computed on taxable value.</p>
          </Field>
          <Field id="tdsRate" label="Rate %">
            <Input
              id="tdsRate"
              value={values.tdsRate}
              onChange={(event) => onChange("tdsRate", event.target.value)}
              className="text-right font-mono"
              inputMode="decimal"
            />
          </Field>
          <Field label="Base">
            <Input
              value={paiseToInrExact(totals.subtotalPaise).replace("₹", "")}
              disabled
              className="text-right font-mono"
            />
          </Field>
          <Field label="TDS amount">
            <Input
              value={paiseToInrExact(tdsAmountPaise).replace("₹", "")}
              disabled
              className="text-right font-mono"
            />
          </Field>
        </div>
        <p className="mt-2 text-xs text-muted">
          Goods purchases under <span className="font-mono">₹50L</span> aggregate from this vendor
          this FY do not attract 194Q.
        </p>

        <div className="mt-4 grid gap-3 md:grid-cols-3">
          <SummaryTile label="Taxable value" value={paiseToInrExact(totals.subtotalPaise)} />
          <SummaryTile label={taxSummary.label} value={taxSummary.value} />
          <SummaryTile label="Bill total" value={paiseToInrExact(totals.totalPaise)} strong />
        </div>
      </SectionCard>

      <SectionCard
        step="6"
        title="Attachments & notes"
        description="Original bill + any supporting docs the CA might ask for later."
      >
        <BillAttachmentsCard billId={savedBillId} />
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <Field id="vendorNote" label="Vendor note (visible on bill)">
            <textarea
              id="vendorNote"
              value={values.vendorNote}
              onChange={(event) => onChange("vendorNote", event.target.value)}
              className="min-h-24 w-full rounded-sm border border-line bg-paper px-3 py-2 text-sm text-ink transition hover:border-line-2 focus-visible:border-sky-600 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-sky-100"
            />
          </Field>
          <Field id="internalNote" label="Internal note (team only)">
            <textarea
              id="internalNote"
              value={values.internalNote}
              onChange={(event) => onChange("internalNote", event.target.value)}
              className="min-h-24 w-full rounded-sm border border-line bg-paper px-3 py-2 text-sm text-ink transition hover:border-line-2 focus-visible:border-sky-600 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-sky-100"
            />
          </Field>
        </div>
      </SectionCard>

      <SectionCard
        step="7"
        title="Approval routing"
        description="Above ₹1L crosses your founder-approval threshold - OTP step-up required."
        action={
          <Button intent="ghost" size="sm" className="border border-line">
            <Settings className="h-4 w-4" />
            Edit thresholds
          </Button>
        }
      >
        {/* Routing preview cards intentionally show roles, not real
            names — the bill hasn't been saved yet so there's no actor
            to bind. Real names appear on the bill-detail Approval
            Routing once the bill exists (V1.x will pipe `users.name`
            through `bills.get`). */}
        <div className="grid gap-3 md:grid-cols-3">
          <RoutingNode
            tone="ok"
            title="Operator"
            role="Captured + matched"
            when="On submit"
          />
          <RoutingNode
            tone="warn"
            title="Founder"
            role="OTP step-up"
            when="On approve"
          />
          <RoutingNode
            tone="neutral"
            title="Payment recorded"
            role="Bank · NEFT / RTGS"
            when="After founder approval"
          />
        </div>
        <div className="mt-4 flex items-start gap-2 rounded-md border border-sky-100 bg-sky-50 p-3 text-sm text-ink-2">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-sky-700" />
          <p>
            <strong className="text-ink">Reminder.</strong> Flint never auto-pays vendor bills.
            After approval, we record the bill and generate payment advice - you transfer via your
            bank app.
          </p>
        </div>
      </SectionCard>
    </div>
  );
}

function TaxModeButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`min-w-0 truncate rounded-sm border px-3 py-2 text-left text-sm font-semibold transition ${
        active
          ? "border-sky-600 bg-sky-50 text-sky-700"
          : "border-line bg-paper text-muted hover:border-line-2 hover:text-ink-2"
      }`}
    >
      {children}
    </button>
  );
}

function ToggleRow({
  title,
  body,
  checked,
  onChange,
}: {
  title: string;
  body: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="mt-4 flex items-center justify-between gap-4 rounded-md border border-line bg-raised p-3">
      <div>
        <p className="text-sm font-semibold text-ink">{title}</p>
        <p className="mt-1 text-xs text-muted">{body}</p>
      </div>
      <button
        type="button"
        aria-pressed={checked}
        onClick={() => onChange(!checked)}
        className={`relative h-6 w-11 shrink-0 rounded-full transition ${checked ? "bg-ok" : "bg-line-2"}`}
      >
        <span
          className={`absolute top-1 h-4 w-4 rounded-full bg-paper transition ${checked ? "left-6" : "left-1"}`}
        />
      </button>
    </div>
  );
}

function RoutingNode({
  tone,
  title,
  role,
  when,
}: {
  tone: "ok" | "warn" | "neutral";
  title: string;
  role: string;
  when: string;
}) {
  const toneClasses = {
    ok: "border-ok-100 bg-ok-50 text-ok",
    warn: "border-warn-100 bg-warn-50 text-warn",
    neutral: "border-line bg-raised text-muted",
  }[tone];
  const Icon = tone === "neutral" ? CircleDollarSign : MessageSquareText;
  return (
    <div className="rounded-md border border-line bg-paper p-3">
      <span className={`grid h-8 w-8 place-items-center rounded-full border ${toneClasses}`}>
        <Icon className="h-4 w-4" />
      </span>
      <p className="mt-3 text-sm font-semibold text-ink">{title}</p>
      <p className="text-xs text-muted">{role}</p>
      <p className="mt-1 font-mono text-xs text-muted">{when}</p>
    </div>
  );
}

function SummaryTile({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <div className="rounded-md border border-line bg-raised p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted">{label}</p>
      <p
        className={`mt-2 font-mono ${strong ? "text-lg font-semibold text-ink" : "text-sm font-semibold text-ink-2"}`}
      >
        {value}
      </p>
    </div>
  );
}

function tdsSectionLabel(value: string): string {
  if (value === "194Q") return "194Q - purchase of goods - 0.1%";
  if (value === "194C") return "194C - contract / sub-contract - 1%";
  if (value === "194J") return "194J - professional fees - 10%";
  return "None - raw material goods";
}

function defaultTdsRate(value: string): string {
  if (value === "194Q") return "0.1";
  if (value === "194C") return "1";
  if (value === "194J") return "10";
  return "0";
}

function computeTdsPaise(basePaise: bigint, rate: string): bigint {
  const bps = parsePercentBps(rate);
  return bps === null ? 0n : (basePaise * bps) / 10000n;
}

function parsePercentBps(value: string): bigint | null {
  const cleaned = value.trim();
  if (!/^\d+(\.\d{0,2})?$/.test(cleaned)) return null;
  const [whole = "0", fraction = ""] = cleaned.split(".");
  return BigInt(whole || "0") * 100n + BigInt((fraction + "00").slice(0, 2));
}

function taxSummaryTile(
  mode: NewBillFormValues["taxMode"],
  halfTaxPaise: bigint,
  taxPaise: bigint,
): { label: string; value: string } {
  if (mode === "inter") return { label: "IGST", value: paiseToInrExact(taxPaise) };
  if (mode === "exempt") return { label: "GST", value: paiseToInrExact(0n) };
  return {
    label: "CGST + SGST",
    value: `${paiseToInrExact(halfTaxPaise)} + ${paiseToInrExact(halfTaxPaise)}`,
  };
}

function taxModeHelp(mode: NewBillFormValues["taxMode"], companyState: string): string {
  if (mode === "inter") return "Inter-state supply: IGST applies on the bill.";
  if (mode === "exempt") return "Non-GST or exempt bill: GST is not claimed as input tax credit.";
  return companyState
    ? `Vendor is in ${companyState} - same state as you. Intra-state CGST + SGST split applies.`
    : "Same-state supply: intra-state CGST + SGST split applies.";
}
